import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchHints, isAPIError, getErrorMessage } from '../utils/api';
import { getHintLabels, resolveHintTypeLabels } from '../config/hintConfig';
import type { HintData } from '../types/game';
import type { StockCategoryFilter } from '../types/stockCategories';
import type { HintsAPIResponse } from '../types/api';

interface UseHintsReturn {
  hints: HintData[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseHintsOptions {
  autoFetch?: boolean;
  category?: StockCategoryFilter;
  date?: string;
}

const buildHints = (apiHints: HintsAPIResponse['hints'], category?: string): HintData[] => {
  const selectedTypes = apiHints.selectedHintTypes;

  if (selectedTypes) {
    // Dynamic hint types: use labels from the registry
    const resolved = resolveHintTypeLabels(selectedTypes);
    const hintKeys = ['hint1', 'hint2', 'hint3', 'hint4'] as const;

    return hintKeys.map((key, index) => ({
      type: selectedTypes[index],
      content: apiHints[key],
      unlockAttempt: index + 1,
      isVisible: false,
      title: resolved[index].displayName,
    }));
  }

  // Legacy fallback: use fixed labels based on category
  const labels = getHintLabels(category ?? 'popular');
  return [
    {
      type: 'revenue',
      content: apiHints.hint1,
      unlockAttempt: 1,
      isVisible: false,
      title: labels.hint1,
      maxVisibleBullets: 1
    },
    {
      type: 'sector',
      content: apiHints.hint2,
      unlockAttempt: 2,
      isVisible: false,
      title: labels.hint2,
      maxVisibleBullets: 1
    },
    {
      type: 'founded',
      content: apiHints.hint3,
      unlockAttempt: 3,
      isVisible: false,
      title: labels.hint3
    },
    {
      type: 'description',
      content: apiHints.hint4,
      unlockAttempt: 4,
      isVisible: false,
      title: labels.hint4
    }
  ];
};

const normaliseHintsResponse = (response: any, category?: string): HintData[] => {
  const payload: HintsAPIResponse | undefined =
    response?.data && typeof response.data === 'object'
      ? response.data
      : response;

  if (!payload?.hints) {
    throw new Error('Invalid hints response');
  }

  return buildHints(payload.hints, category);
};

export function useHints({
  autoFetch = true,
  category,
  date
}: UseHintsOptions = {}): UseHintsReturn {
  const effectiveCategory = category ?? 'spy';

  const queryKey = useMemo(
    () => ['hints', effectiveCategory, date ?? 'current'],
    [effectiveCategory, date]
  );

  const query = useQuery({
    queryKey,
    enabled: autoFetch,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await fetchHints(effectiveCategory, date);
      if (!response.success) {
        throw new Error('Failed to fetch hints');
      }
      return normaliseHintsResponse(response, effectiveCategory);
    }
  });

  const errorMessage = useMemo(() => {
    if (!query.error) {
      return null;
    }

    if (isAPIError(query.error)) {
      return getErrorMessage(query.error);
    }

    if (query.error instanceof Error) {
      return query.error.message;
    }

    return 'Failed to load hints';
  }, [query.error]);

  const refetch = useCallback(async () => {
    const result = await query.refetch({ throwOnError: true });
    if (result.error) {
      throw result.error;
    }
  }, [query]);

  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'assertive');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = `Error loading hints: ${errorMessage}`;

    document.body.appendChild(announcement);

    const timeout = window.setTimeout(() => {
      document.body.removeChild(announcement);
    }, 3000);

    return () => {
      clearTimeout(timeout);
      if (announcement.parentNode) {
        announcement.parentNode.removeChild(announcement);
      }
    };
  }, [errorMessage]);

  return {
    hints: query.data ?? [],
    isLoading: query.isLoading,
    error: errorMessage,
    refetch
  };
}

export default useHints;
